import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { mediaList } from "@/mocks/movies";
import { typeToSlug } from "@/mocks/categories";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb, { type Crumb } from "@/components/feature/Breadcrumb";
import Modal from "@/components/base/Modal";
import useDocumentMeta from "@/hooks/useDocumentMeta";

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 border-b border-background-200 py-3 last:border-b-0">
      <span className="w-20 shrink-0 text-sm text-foreground-500">{label}</span>
      <span className="text-sm font-medium text-foreground-900">{value}</span>
    </div>
  );
}

export default function Detail() {
  const { id } = useParams();
  const item = mediaList.find((m) => m.id === id);
  const [playOpen, setPlayOpen] = useState(false);
  const [collected, setCollected] = useState(false);

  useDocumentMeta({
    title: item ? `${item.title} - 详细介绍与在线观看 - 蘑菇视频` : "影视详情 - 蘑菇视频",
    description: item
      ? `${item.title}：${item.genre}${item.type}，${item.region}出品，${item.year}年上映，由${item.director}执导，${item.actors.join("、")}主演，评分${item.rating}。${item.synopsis.slice(0, 60)}…`
      : "蘑菇视频影视详情页",
    keywords: item ? `${item.title},${item.genre},蘑菇视频,在线观看,影视详情` : "蘑菇视频,影视详情",
  });

  const related = useMemo(() => {
    if (!item) return [];
    return mediaList
      .filter((m) => m.id !== item.id && (m.genre === item.genre || m.actors.some((a) => item.actors.includes(a)) || m.type === item.type))
      .sort((a, b) => b.hot - a.hot)
      .slice(0, 6);
  }, [item]);

  if (!item) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500 text-3xl">
          <i className="ri-film-line" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 text-xl font-semibold text-foreground-950">未找到该影视内容</h1>
        <p className="mt-2 text-sm text-foreground-500">该作品可能已下架或链接有误，不妨回到首页看看更多精彩内容。</p>
        <Link
          to="/"
          className="mt-6 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-home-4-line" aria-hidden="true"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const crumbs: Crumb[] = [
    { label: item.type, to: `/category/${typeToSlug[item.type]}` },
    { label: item.title },
  ];

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 md:px-8 py-5 md:py-6">
          <Breadcrumb items={crumbs} />
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div className="lg:col-span-2">
            <div className="flex flex-col gap-6 sm:flex-row">
              <div className="relative w-full shrink-0 overflow-hidden rounded-lg bg-background-200 sm:w-56 md:w-64">
                <div className="aspect-[2/3] w-full">
                  <img
                    src={item.poster}
                    alt={`${item.title} 高清封面`}
                    title={`${item.title} - 详细介绍与在线观看`}
                    className="h-full w-full object-top"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setPlayOpen(true)}
                  className="group absolute inset-0 flex items-center justify-center bg-secondary-950/40 transition-colors hover:bg-secondary-950/55 cursor-pointer"
                  aria-label={`播放 ${item.title}`}
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 text-3xl transition-transform group-hover:scale-110">
                    <i className="ri-play-fill" aria-hidden="true"></i>
                  </span>
                </button>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-primary-500 px-2 py-0.5 text-xs font-semibold text-background-50">{item.type}</span>
                  <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-800">{item.genre}</span>
                  {item.tags.map((t) => (
                    <span key={t} className="rounded-md bg-background-200 px-2 py-0.5 text-xs text-foreground-600">{t}</span>
                  ))}
                </div>

                <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl lg:text-4xl">
                  {item.title}
                </h1>

                <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                  <span className="flex items-center gap-1.5 font-heading text-xl font-bold text-primary-600">
                    <i className="ri-star-fill text-base" aria-hidden="true"></i>
                    {item.rating.toFixed(1)}
                  </span>
                  <span className="text-foreground-500">热度 {item.hot}</span>
                  <span className="text-foreground-500">更新于 {item.updateDate}</span>
                </div>

                <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-foreground-600">{item.synopsis}</p>

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setPlayOpen(true)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                  >
                    <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                    立即观看
                  </button>
                  <button
                    type="button"
                    onClick={() => setCollected((v) => !v)}
                    className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
                      collected
                        ? "border-accent-300 bg-accent-100 text-accent-700"
                        : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    <i className={collected ? "ri-bookmark-fill" : "ri-bookmark-line"} aria-hidden="true"></i>
                    {collected ? "已收藏" : "收藏"}
                  </button>
                </div>
                {collected && (
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-accent-700">
                    <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                    已加入收藏（演示效果，不会保存个人数据）
                  </p>
                )}
              </div>
            </div>

            <section className="mt-8" aria-label="剧情简介">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-primary-500"></span>
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-loose text-foreground-600">{item.synopsis}</p>
            </section>

            <section className="mt-8" aria-label="播放说明">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-primary-500"></span>
                播放说明
              </h2>
              <div className="mt-3 flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600 text-lg">
                  <i className="ri-error-warning-line" aria-hidden="true"></i>
                </span>
                <div className="text-sm leading-relaxed text-foreground-600">
                  <p>本站为演示站点，暂未接入正式播放接口。点击「立即观看」将展示播放说明，不会真正开始播放视频。</p>
                  <p className="mt-1.5">正式上线后，播放按钮将跳转至对应的播放页面或第三方合作平台，具体以实际页面提示为准。</p>
                </div>
              </div>
            </section>

            <section className="mt-8" aria-label="所属分类与标签">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-primary-500"></span>
                所属分类与标签
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to={`/category/${typeToSlug[item.type]}`}
                  className="flex items-center gap-1 rounded-md bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
                >
                  <i className={item.type === "电影" ? "ri-film-line" : item.type === "电视剧" ? "ri-tv-2-line" : "ri-mic-line"} aria-hidden="true"></i>
                  {item.type}频道
                </Link>
                <Link
                  to={`/category/${typeToSlug[item.type]}/${encodeURIComponent(item.genre)}`}
                  className="flex items-center gap-1 rounded-md bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200 cursor-pointer"
                >
                  <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                  {item.genre}
                </Link>
                <Link
                  to={`/category/${typeToSlug[item.type]}/${encodeURIComponent(item.region)}`}
                  className="flex items-center gap-1 rounded-md bg-background-200 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-300 cursor-pointer"
                >
                  <i className="ri-earth-line" aria-hidden="true"></i>
                  {item.region}
                </Link>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1" aria-label="详细信息">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-file-list-3-line text-primary-500" aria-hidden="true"></i>
                详细信息
              </h2>
              <div className="mt-3">
                <InfoRow label="导演" value={item.director} />
                <InfoRow label="主演" value={item.actors.join("、")} />
                <InfoRow label="类型" value={`${item.type} · ${item.genre}`} />
                <InfoRow label="地区" value={item.region} />
                <InfoRow label="上映时间" value={`${item.year}年`} />
                <InfoRow label="时长" value={item.duration} />
                <InfoRow label={item.type === "电影" ? "片长规格" : "集数"} value={item.type === "电影" ? item.duration : `全 ${item.episodes} 集`} />
                <InfoRow label="评分" value={`${item.rating.toFixed(1)} 分（示例数据）`} />
                <InfoRow label="更新日期" value={item.updateDate} />
              </div>
              <button
                type="button"
                onClick={() => setPlayOpen(true)}
                className="mt-4 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                立即观看
              </button>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-12" aria-label="相关推荐">
            <div className="mb-5 flex items-center gap-2">
              <span className="h-5 w-1 rounded-full bg-accent-500"></span>
              <h2 className="text-lg font-bold text-foreground-950">相关推荐</h2>
              <span className="text-xs text-foreground-500">同类型 · 同主演</span>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {related.map((r) => (
                <MovieCard key={r.id} item={r} />
              ))}
            </div>
          </section>
        )}
      </div>

      <Modal open={playOpen} onClose={() => setPlayOpen(false)} title={`播放说明 · ${item.title}`}>
        <p>
          当前为演示环境，尚未接入真实的视频播放接口，因此点击「立即观看」不会开始播放视频内容，敬请谅解。
        </p>
        <p className="mt-3">
          正式版本上线后，我们将提供高清在线播放体验，播放按钮将跳转至对应的播放页或经过授权的合作平台。感谢你对蘑菇视频的支持与理解。
        </p>
        <button
          type="button"
          onClick={() => setPlayOpen(false)}
          className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          我知道了
        </button>
      </Modal>
    </div>
  );
}